import { motion, AnimatePresence } from 'framer-motion';

// ── A blow lands on a standard: shockwave, cracks, sparks ──
const CrownLossEffect = ({ active }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        initial={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'none', borderRadius: 13, overflow: 'hidden' }}
        data-testid="crown-loss-effect"
      >
        {/* blood wash */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0.3, 0] }}
          transition={{ duration: 0.6 }}
          style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(220,38,38,0.55), rgba(120,10,10,0.2) 70%)' }}
        />
        {/* impact ring */}
        <motion.div
          initial={{ scale: 0.2, opacity: 0.95 }} animate={{ scale: 2.4, opacity: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          style={{ position: 'absolute', top: '50%', left: '50%', width: 70, height: 70, marginTop: -35, marginLeft: -35,
            borderRadius: '50%', border: '3px solid rgba(255,90,70,0.9)', boxShadow: '0 0 22px rgba(255,80,60,0.8)' }}
        />
        {/* fracture lines */}
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {['M20 0 L34 26 L24 34 L40 60', 'M78 0 L66 22 L76 32 L60 60', 'M50 6 L46 30 L56 38 L50 58'].map((d, i) => (
            <motion.path key={i} d={d} fill="none" stroke="rgba(255,190,180,0.85)" strokeWidth="1.1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: [0, 1, 0] }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
            />
          ))}
        </svg>
        {/* sparks */}
        {Array.from({ length: 10 }, (_, i) => {
          const a = (i / 10) * Math.PI * 2;
          return (
            <motion.div key={i}
              initial={{ x: 0, y: 0, opacity: 1 }}
              animate={{ x: Math.cos(a) * 66, y: Math.sin(a) * 40, opacity: 0 }}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              style={{ position: 'absolute', top: '50%', left: '50%', width: 3, height: 3, borderRadius: '50%',
                background: '#ffb4a2', boxShadow: '0 0 6px #ff6b4a' }}
            />
          );
        })}
      </motion.div>
    )}
  </AnimatePresence>
);

// ── A crown is won: gold burst and rising light ──
const CrownGainEffect = ({ active }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        initial={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'none', borderRadius: 13, overflow: 'hidden' }}
        data-testid="crown-gain-effect"
      >
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.75, 0.35, 0] }}
          transition={{ duration: 0.7 }}
          style={{ position: 'absolute', inset: 0,
            background: 'radial-gradient(ellipse at center, rgba(255,215,0,0.5), transparent 72%)',
            boxShadow: 'inset 0 0 30px rgba(255,200,40,0.6)' }}
        />
        {Array.from({ length: 12 }, (_, i) => (
          <motion.div key={i}
            initial={{ y: 18, opacity: 0, scale: 0.4 }}
            animate={{ y: -34, opacity: [0, 1, 0], scale: 1 }}
            transition={{ duration: 0.85, delay: i * 0.035, ease: 'easeOut' }}
            style={{ position: 'absolute', left: `${8 + i * 7.5}%`, bottom: '18%', fontSize: 11, color: '#ffd700',
              textShadow: '0 0 8px rgba(255,200,40,0.9)' }}
          >
            ✦
          </motion.div>
        ))}
        <motion.div
          initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: [0.3, 1.25, 1], opacity: [0, 1, 0] }}
          transition={{ duration: 0.8 }}
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            fontSize: 30, filter: 'drop-shadow(0 0 14px rgba(255,200,40,0.95))' }}
        >
          ♛
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── The shield holds: an energy dome takes the hit ──
const ShieldBlockEffect = ({ active }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        initial={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: -4, zIndex: 30, pointerEvents: 'none' }}
        data-testid="shield-block-effect"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: [0, 1, 0.7, 0], scale: [0.85, 1.06, 1.02, 1.1] }}
          transition={{ duration: 0.75 }}
          style={{ position: 'absolute', inset: 0, borderRadius: 16,
            border: '2px solid rgba(147,197,253,0.95)',
            background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.22), transparent 70%)',
            boxShadow: '0 0 28px 8px rgba(59,130,246,0.55), inset 0 0 22px rgba(147,197,253,0.5)' }}
        />
        {/* hex weave flashing through the dome */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.55, 0] }}
          transition={{ duration: 0.7 }}
          style={{ position: 'absolute', inset: 0, borderRadius: 16,
            backgroundImage: 'repeating-linear-gradient(60deg, rgba(147,197,253,0.4) 0 1px, transparent 1px 12px), repeating-linear-gradient(-60deg, rgba(147,197,253,0.4) 0 1px, transparent 1px 12px)' }}
        />
        {[0, 1].map(i => (
          <motion.div key={i}
            initial={{ scale: 0.3, opacity: 0.8 }} animate={{ scale: 1.9, opacity: 0 }}
            transition={{ duration: 0.7, delay: i * 0.18, ease: 'easeOut' }}
            style={{ position: 'absolute', top: '50%', left: '50%', width: 60, height: 60, marginTop: -30, marginLeft: -30,
              borderRadius: '50%', border: '2px solid rgba(96,165,250,0.85)' }}
          />
        ))}
      </motion.div>
    )}
  </AnimatePresence>
);

// ── The jester takes the stage ──
const JollyEffect = ({ active }) => (
  <AnimatePresence>
    {active && (
      <motion.div
        initial={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'absolute', inset: 0, zIndex: 30, pointerEvents: 'none', borderRadius: 13, overflow: 'hidden' }}
        data-testid="jolly-effect"
      >
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.6, 0.25, 0] }}
          transition={{ duration: 0.8 }}
          style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(192,132,252,0.45), transparent 72%)',
            border: '1px solid rgba(216,180,254,0.6)', borderRadius: 13 }}
        />
        {Array.from({ length: 14 }, (_, i) => (
          <motion.div key={i}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
            animate={{ x: (i % 2 ? 1 : -1) * (20 + (i % 5) * 16), y: -26 - (i % 4) * 12, opacity: 0, rotate: 260 }}
            transition={{ duration: 0.9, delay: i * 0.02, ease: 'easeOut' }}
            style={{ position: 'absolute', top: '55%', left: '50%', width: 5, height: 7, borderRadius: 1,
              background: ['#f0abfc', '#fde68a', '#67e8f9', '#fca5a5'][i % 4] }}
          />
        ))}
      </motion.div>
    )}
  </AnimatePresence>
);

// ── A crown flies from one standard to another ──
const CrownPassAnimation = ({ active, fromPos, toPos }) => {
  if (!active) return null;
  return (
    <motion.div
      initial={{ opacity: 1, x: fromPos?.x || 0, y: fromPos?.y || 0, scale: 1 }}
      animate={{ opacity: [1, 1, 0], x: toPos?.x || 0, y: toPos?.y || 0, scale: [1, 1.4, 0.8] }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
      style={{ position: 'fixed', zIndex: 100, pointerEvents: 'none' }}
      data-testid="crown-pass-animation"
    >
      <svg width="30" height="30" viewBox="0 0 24 24">
        <defs>
          <linearGradient id="flyingCrown" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <path d="M2 20h20v2H2v-2zM4 17l2-10 4 4 2-6 2 6 4-4 2 10H4z" fill="url(#flyingCrown)" />
      </svg>
    </motion.div>
  );
};

// ── The standard reels from the blow ──
const PlayerShake = ({ active, children }) => (
  <motion.div
    animate={active ? { x: [0, -7, 6, -4, 3, -1, 0], rotate: [0, -1.4, 1.2, -0.6, 0.3, 0] } : {}}
    transition={{ duration: 0.5, ease: 'easeOut' }}
  >
    {children}
  </motion.div>
);

// Helper: parse action type from feedback message
export const parseActionType = (message, cardValue) => {
  if (!message) return null;
  const lower = message.toLowerCase();
  if (lower.includes('shield blocked')) return 'shield_block';
  if (lower.includes('lost') && lower.includes('crown')) return 'crown_loss';
  if (lower.includes('frozen') || lower.includes('imprisoned')) return 'crown_loss';
  if (lower.includes('gave') && lower.includes('crown')) return 'crown_pass';
  if (lower.includes('gained') && lower.includes('crown')) return 'crown_gain';
  if (lower.includes('gained') && lower.includes('shield')) return 'shield_gain';
  if (lower.includes('jester') || lower.includes('jolly')) return 'jolly';
  if (cardValue >= 1 && cardValue <= 3) return 'crown_loss';
  if (cardValue === 10) return 'crown_gain';
  return null;
};

// Helper: figure out which player name is mentioned as target in a "gave crown to X" message
export const parseTargetPlayer = (message) => {
  const match = message?.match(/gave \d+ crown to (\w+)/i) || message?.match(/crown to (\w+)/i);
  return match ? match[1] : null;
};

export {
  CrownPassAnimation,
  ShieldBlockEffect,
  CrownLossEffect,
  CrownGainEffect,
  JollyEffect,
  PlayerShake,
};
